// Lightweight GTM/dataLayer helper + privacy-safe internal analytics.
// - GTM container is loaded in public/index.html.
// - We also POST anonymous, non-sensitive events to Brivio's own counter
//   (/api/app/track) for the internal Admin "Traffic & Signups" page.
// No PII: we only ever send pathname (never query strings), an anonymous
// visitor id, UTM params, referrer source, and device type.

const dl = () => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const VID_KEY = "brivio_vid";
const UTM_KEY = "brivio_utm";
const TOKEN_KEY = "brivio_app_token";

const getVisitorId = () => {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) {
      v = (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(VID_KEY, v);
    }
    return v;
  } catch { return "anon"; }
};

const deviceType = () => {
  try { return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? "mobile" : "desktop"; }
  catch { return "desktop"; }
};

const referrerSource = () => {
  try {
    const r = document.referrer || "";
    if (!r) return "Direct";
    const h = new URL(r).hostname.replace(/^www\./, "");
    const self = (window.location.hostname || "").replace(/^www\./, "");
    if (self && h.endsWith(self)) return "Direct";
    if (/facebook|fb\.com|instagram|meta|fbclid/i.test(h)) return "Facebook";
    if (/google|bing|duckduckgo|yahoo|ecosia/i.test(h)) return "Google";
    return "Other";
  } catch { return "Other"; }
};

const captureUtm = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    const utm = {};
    ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
      const v = p.get(k);
      if (v) utm[k] = v.slice(0, 80);
    });
    if (Object.keys(utm).length) sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
  } catch { /* no-op */ }
};
const getUtm = () => { try { return JSON.parse(sessionStorage.getItem(UTM_KEY) || "{}"); } catch { return {}; } };

const PAGE_LABELS = [
  [/^\/$/, "homepage"],
  [/^\/how-it-works/, "how it works"],
  [/^\/(search|nearby)/, "nearby providers"],
  [/^\/for-providers/, "for providers"],
  [/^\/about/, "about"],
  [/^\/reviews/, "reviews"],
  [/^\/app\/auth/, "sign up"],
  [/^\/app\/get-help/, "get help"],
  [/^\/app\/provider/, "provider dashboard"],
  [/^\/app\/dashboard/, "app home"],
  [/^\/app/, "app"],
];
const pageLabel = (path) => { const m = PAGE_LABELS.find(([re]) => re.test(path)); return m ? m[1] : path; };

// Fire-and-forget POST to Brivio's internal counter (keepalive so it never blocks navigation).
const postTrack = (event, extra = {}) => {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const token = (() => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } })();
    const path = window.location.pathname; // pathname ONLY — never query (no PII)
    if (path.startsWith("/admin")) return; // don't count admin's own browsing
    const utm = getUtm();
    let source = referrerSource();
    if (source === "Direct" && utm.utm_source) {
      const s = utm.utm_source.toLowerCase();
      source = /facebook|fb|meta|instagram/.test(s) ? "Facebook" : /google/.test(s) ? "Google" : "Other";
    }
    const body = JSON.stringify({
      event, visitor_id: getVisitorId(), route: path, page: pageLabel(path),
      referrer_source: source, device: deviceType(), ...utm,
      ...(extra.cta ? { cta: String(extra.cta).slice(0, 40) } : {}),
    });
    fetch(`${base}/api/app/track`, {
      method: "POST", keepalive: true,
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body,
    }).catch(() => {});
  } catch { /* no-op */ }
};

// Push a named event (GTM dataLayer + internal counter).
export const track = (event, params = {}) => {
  try { dl().push({ event, ...params }); } catch { /* no-op */ }
  postTrack(event, { cta: params.cta });
};

// SPA page view — call on every route change.
export const trackPageView = (path) => {
  try { dl().push({ event: "page_view", page_path: path, page_location: window.location.href }); } catch { /* no-op */ }
  postTrack("page_view");
};

// Map key CTA button/link text to click events via a single delegated listener.
const CLICK_MAP = [
  { test: /sign\s*up\s*free/i, event: "sign_up_free_click" },
  { test: /find\s*nearby\s*providers/i, event: "find_nearby_providers_click" },
  { test: /open\s*app/i, event: "open_app_click" },
];

let clickBound = false;
export const initAnalytics = () => {
  if (typeof document === "undefined") return;
  captureUtm();
  if (clickBound) return;
  clickBound = true;
  document.addEventListener("click", (e) => {
    const el = e.target?.closest?.("a,button");
    if (!el) return;
    // CTA breakdown by source (data-cta attribute) — independent of the funnel events below.
    const ctaEl = e.target?.closest?.("[data-cta]");
    if (ctaEl) { const cta = ctaEl.getAttribute("data-cta"); if (cta) track("cta_click", { cta }); }
    // Funnel click events, matched by button text.
    const label = (el.innerText || el.textContent || "").trim();
    if (!label || label.length > 40) return;
    const hit = CLICK_MAP.find((m) => m.test.test(label));
    if (hit) track(hit.event, { cta_label: label, page_path: window.location.pathname });
  }, true);
};
